import * as THREE from 'three';
import {
  DROPS,
  RIVER_D_MAX,
  RIVER_D_MIN,
  RIVER_LENGTH,
  SPLIT_DROP_D,
  SPLIT_END,
  SPLIT_START,
  TUNNEL_END,
  TUNNEL_RADIUS,
  TUNNEL_START,
  type FlowSample,
  type River,
} from '../game/River';
import { disposeObject3D } from '../utils/dispose';

const ROW_STEP = 0.5;
const COLUMNS = 36;
/** Water extends slightly past the banks so it tucks under the bank slope. */
const WATER_EDGE_S = 1.08;

const waterVertex = /* glsl */ `
attribute vec2 aFlow;
attribute float aSpeed;
attribute float aFoam;
attribute float aWhirl;
attribute float aEdge;
varying vec2 vFlow;
varying float vSpeed;
varying float vFoam;
varying float vWhirl;
varying float vEdge;
varying vec3 vWorld;
#include <fog_pars_vertex>
void main() {
  vFlow = aFlow;
  vSpeed = aSpeed;
  vFoam = aFoam;
  vWhirl = aWhirl;
  vEdge = aEdge;
  vec4 worldPosition = modelMatrix * vec4(position, 1.0);
  vWorld = worldPosition.xyz;
  vec4 mvPosition = viewMatrix * worldPosition;
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const waterFragment = /* glsl */ `
uniform float uTime;
uniform vec3 uDeep;
uniform vec3 uFast;
uniform vec3 uShallow;
uniform vec3 uFoam;
uniform vec2 uTunnel;
varying vec2 vFlow;
varying float vSpeed;
varying float vFoam;
varying float vWhirl;
varying float vEdge;
varying vec3 vWorld;
#include <fog_pars_fragment>

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
// Streaks stretched along the river (z) so the surface reads as flowing water.
float streaks(vec2 p) {
  vec2 q = vec2(p.x * 1.7, p.y * 0.32);
  return noise(q) * 0.6 + noise(q * 2.4 + 7.1) * 0.4;
}

void main() {
  // Two-phase flow map: advect the pattern along the baked flow and cross-fade
  // between two offset phases so it never stretches out.
  float period = 1.6;
  float t0 = fract(uTime / period);
  float t1 = fract(uTime / period + 0.5);
  vec2 p = vWorld.xz;
  float a = streaks(p - vFlow * t0 * period);
  float b = streaks(p - vFlow * t1 * period + vec2(3.7, 11.3));
  float n = mix(a, b, abs(2.0 * t0 - 1.0));

  float speed = clamp(vSpeed, 0.0, 1.0);
  float streak = smoothstep(0.6 - speed * 0.1, 0.8, n) * (0.12 + 0.8 * speed * speed);

  vec3 color = mix(uDeep, uFast, smoothstep(0.35, 0.95, speed));
  color = mix(color, uShallow, smoothstep(0.78, 1.05, vEdge) * 0.7);
  color *= 1.0 - vWhirl * 0.45;
  color += uFoam * streak * 0.6;

  float foamNoise = noise(p * 3.1 + vec2(0.0, uTime * 1.3));
  float foam = clamp(vFoam * (0.55 + 0.6 * foamNoise), 0.0, 1.0);
  color = mix(color, uFoam, foam);

  // Inside the pipe the water only catches a little light.
  float d = -vWorld.z;
  float inside = smoothstep(uTunnel.x - 1.0, uTunnel.x + 2.0, d) * (1.0 - smoothstep(uTunnel.y - 2.0, uTunnel.y + 1.0, d));
  color *= 1.0 - inside * 0.72;

  gl_FragColor = vec4(color, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

function makeSpiralTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2D context for whirlpool texture.');
  ctx.translate(size / 2, size / 2);
  for (let arm = 0; arm < 3; arm += 1) {
    ctx.beginPath();
    for (let i = 0; i <= 80; i += 1) {
      const t = i / 80;
      const angle = arm * ((Math.PI * 2) / 3) + t * Math.PI * 3;
      const radius = (1 - t) * size * 0.47;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = 'rgba(235, 252, 255, 0.55)';
    ctx.lineWidth = 7;
    ctx.lineCap = 'round';
    ctx.stroke();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeCheckerTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2D context for finish texture.');
  for (let x = 0; x < 8; x += 1) {
    for (let y = 0; y < 2; y += 1) {
      ctx.fillStyle = (x + y) % 2 === 0 ? '#ffffff' : '#1b1b1b';
      ctx.fillRect(x * 16, y * 16, 16, 16);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.wrapS = THREE.RepeatWrapping;
  return texture;
}

export class RiverView {
  readonly group = new THREE.Group();
  private readonly waterMaterial: THREE.ShaderMaterial;
  private readonly waterGeometry: THREE.BufferGeometry;
  private readonly whirlDecals: THREE.Mesh[] = [];
  private spray: THREE.InstancedMesh | null = null;
  private readonly sprayBase: THREE.Vector3[] = [];
  private readonly sprayMatrix = new THREE.Matrix4();
  private readonly sprayQuat = new THREE.Quaternion();
  private readonly sprayScale = new THREE.Vector3();
  private readonly sprayPos = new THREE.Vector3();
  private readonly sample: FlowSample = { x: 0, z: 0, speed: 0, whirl: 0 };

  constructor(
    private readonly river: River,
    private readonly rng: () => number,
  ) {
    this.waterGeometry = this.buildWaterGeometry();
    this.waterMaterial = new THREE.ShaderMaterial({
      vertexShader: waterVertex,
      fragmentShader: waterFragment,
      fog: true,
      uniforms: THREE.UniformsUtils.merge([
        THREE.UniformsLib.fog,
        {
          uTime: { value: 0 },
          uDeep: { value: new THREE.Color('#1f7f93') },
          uFast: { value: new THREE.Color('#5fd0d6') },
          uShallow: { value: new THREE.Color('#7fb7a4') },
          uFoam: { value: new THREE.Color('#f4fbff') },
          uTunnel: { value: new THREE.Vector2(TUNNEL_START, TUNNEL_END) },
        },
      ]),
    });
    this.bakeFlow();
    this.group.add(new THREE.Mesh(this.waterGeometry, this.waterMaterial));

    this.buildBanks();
    this.buildRocks();
    this.buildWhirlpools();
    this.buildTrees();
    this.buildIsland();
    this.buildTunnel();
    this.buildSpray();
    this.buildGate(RIVER_LENGTH, 'finish');
  }

  update(_delta: number, time: number): void {
    this.waterMaterial.uniforms.uTime.value = time;
    for (const decal of this.whirlDecals) decal.rotation.z = time * 2.2;
    if (this.spray) {
      // Puffs rise and swell from the plunge pool, then restart.
      this.sprayBase.forEach((base, i) => {
        const t = (time * 0.9 + i * 0.137) % 1;
        this.sprayPos.set(base.x, base.y + t * 1.6, base.z - t * 0.8);
        this.sprayScale.setScalar((0.5 + t * 1.1) * (1 - t * t));
        this.sprayMatrix.compose(this.sprayPos, this.sprayQuat, this.sprayScale);
        this.spray?.setMatrixAt(i, this.sprayMatrix);
      });
      this.spray.instanceMatrix.needsUpdate = true;
    }
  }

  private buildSpray(): void {
    const perDrop = 18;
    for (const [dropD] of DROPS) {
      const d = dropD + 1.2;
      for (let k = 0; k < perDrop; k += 1) {
        const s = -0.9 + (1.8 * (k + this.rng() * 0.6)) / perDrop;
        const x = this.river.xAt(d, s);
        this.sprayBase.push(new THREE.Vector3(x, this.river.heightAt(d, x) + 0.1, -d - this.rng() * 0.6));
      }
    }
    // The shortcut's own little waterfall.
    const d = SPLIT_DROP_D + 1.2;
    const islandX = this.river.islandX(d) ?? 0;
    const shore = islandX + this.river.islandHalfWidth(d);
    const bank = this.river.xAt(d, 1);
    for (let k = 0; k < 7; k += 1) {
      const x = shore + ((bank - shore) * (k + 0.5)) / 7;
      this.sprayBase.push(new THREE.Vector3(x, this.river.heightAt(d, x) + 0.1, -d - this.rng() * 0.6));
    }
    this.spray = new THREE.InstancedMesh(
      new THREE.SphereGeometry(0.45, 10, 8),
      new THREE.MeshBasicMaterial({ color: '#f4fbff', transparent: true, opacity: 0.55, depthWrite: false }),
      this.sprayBase.length,
    );
    this.spray.frustumCulled = false;
    this.group.add(this.spray);
    this.update(0, 0);
  }

  /** Re-sample the flow field into the water mesh (after tuning changes). */
  bakeFlow(): void {
    const pos = this.waterGeometry.getAttribute('position') as THREE.BufferAttribute;
    const flow = this.waterGeometry.getAttribute('aFlow') as THREE.BufferAttribute;
    const speed = this.waterGeometry.getAttribute('aSpeed') as THREE.BufferAttribute;
    const foam = this.waterGeometry.getAttribute('aFoam') as THREE.BufferAttribute;
    const whirl = this.waterGeometry.getAttribute('aWhirl') as THREE.BufferAttribute;
    const max = this.river.maxSpeed();
    for (let i = 0; i < pos.count; i += 1) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      this.river.flowAt(x, z, this.sample);
      flow.setXY(i, this.sample.x, this.sample.z);
      speed.setX(i, this.sample.speed / max);
      whirl.setX(i, this.sample.whirl);

      let foamAmount = 0;
      for (const rock of this.river.rocks) {
        const gap = Math.hypot(x - rock.x, z - rock.z) - rock.r;
        if (gap < 0.9) {
          // Bow wave on the upstream face is foamier than the sides.
          const upstream = z < rock.z ? 0.6 : 1;
          foamAmount = Math.max(foamAmount, (1 - Math.max(0, gap) / 0.9) * upstream);
        }
      }
      // White water down each waterfall face and in the plunge pool below.
      foamAmount = Math.max(foamAmount, this.river.plungeAt(-z, x) * (0.55 + 0.45 * Math.abs(Math.sin(x * 2.1 - z))));
      foamAmount = Math.max(foamAmount, this.river.crestAt(-z) * 0.75);
      foam.setX(i, foamAmount);
    }
    for (const attr of [flow, speed, foam, whirl]) attr.needsUpdate = true;
  }

  private buildWaterGeometry(): THREE.BufferGeometry {
    const rows = Math.ceil((RIVER_D_MAX - RIVER_D_MIN) / ROW_STEP) + 1;
    const count = rows * COLUMNS;
    const positions = new Float32Array(count * 3);
    const edges = new Float32Array(count);
    for (let row = 0; row < rows; row += 1) {
      const d = RIVER_D_MIN + row * ROW_STEP;
      for (let col = 0; col < COLUMNS; col += 1) {
        const s = -WATER_EDGE_S + (2 * WATER_EDGE_S * col) / (COLUMNS - 1);
        const i = row * COLUMNS + col;
        const x = this.river.xAt(d, s);
        positions[i * 3] = x;
        positions[i * 3 + 1] = this.river.heightAt(d, x);
        positions[i * 3 + 2] = -d;
        edges[i] = Math.abs(s);
      }
    }
    const indices: number[] = [];
    for (let row = 0; row < rows - 1; row += 1) {
      for (let col = 0; col < COLUMNS - 1; col += 1) {
        const a = row * COLUMNS + col;
        const b = a + 1;
        const c = a + COLUMNS;
        const e = c + 1;
        indices.push(a, b, c, b, e, c);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aEdge', new THREE.BufferAttribute(edges, 1));
    geometry.setAttribute('aFlow', new THREE.BufferAttribute(new Float32Array(count * 2), 2));
    geometry.setAttribute('aSpeed', new THREE.BufferAttribute(new Float32Array(count), 1));
    geometry.setAttribute('aFoam', new THREE.BufferAttribute(new Float32Array(count), 1));
    geometry.setAttribute('aWhirl', new THREE.BufferAttribute(new Float32Array(count), 1));
    geometry.setIndex(indices);
    geometry.computeBoundingSphere();
    return geometry;
  }

  private buildBanks(): void {
    // Each bank: waterline (under water) -> muddy lip -> grass -> far grass.
    const profile: [offset: number, y: number, color: string][] = [
      [-0.2, -0.35, '#6d5a3c'],
      [0.9, 0.42, '#8a7650'],
      [1.8, 0.5, '#5f9a45'],
      [60, 0.5, '#4e8a3a'],
    ];
    const rows = Math.ceil((RIVER_D_MAX - RIVER_D_MIN + 20) / 0.5) + 1;
    for (const side of [-1, 1]) {
      const positions: number[] = [];
      const colors: number[] = [];
      const color = new THREE.Color();
      for (let row = 0; row < rows; row += 1) {
        const d = RIVER_D_MIN - 10 + row * 0.5;
        const edge = this.river.xAt(d, side);
        const base = this.river.heightAt(d, side * Infinity);
        for (const [offset, y, hex] of profile) {
          positions.push(edge + side * offset, base + y, -d);
          color.set(hex);
          colors.push(color.r, color.g, color.b);
        }
      }
      const cols = profile.length;
      const indices: number[] = [];
      for (let row = 0; row < rows - 1; row += 1) {
        for (let col = 0; col < cols - 1; col += 1) {
          const a = row * cols + col;
          const b = a + 1;
          const c = a + cols;
          const e = c + 1;
          if (side > 0) indices.push(a, b, c, b, e, c);
          else indices.push(a, c, b, b, c, e);
        }
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      geometry.setIndex(indices);
      geometry.computeVertexNormals();
      const mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, flatShading: true }),
      );
      mesh.receiveShadow = true;
      this.group.add(mesh);
    }
  }

  private buildRocks(): void {
    const material = new THREE.MeshStandardMaterial({ color: '#8b8a84', roughness: 0.85, flatShading: true });
    for (const rock of this.river.rocks) {
      const geometry = new THREE.IcosahedronGeometry(rock.r, 1);
      const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
      // Jitter shared vertices consistently so the rock stays closed.
      const offsets = new Map<string, number>();
      for (let i = 0; i < pos.count; i += 1) {
        const key = `${pos.getX(i).toFixed(3)},${pos.getY(i).toFixed(3)},${pos.getZ(i).toFixed(3)}`;
        let k = offsets.get(key);
        if (k === undefined) {
          k = 0.82 + this.rng() * 0.3;
          offsets.set(key, k);
        }
        pos.setXYZ(i, pos.getX(i) * k, pos.getY(i) * k, pos.getZ(i) * k);
      }
      geometry.computeVertexNormals();
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(rock.x, this.river.heightAt(rock.d, rock.x) + 0.05, rock.z);
      mesh.scale.y = 0.62;
      mesh.rotation.y = this.rng() * Math.PI;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.group.add(mesh);
    }
  }

  /** The culvert: a concrete pipe with a road embankment over it and a few lamps inside. */
  private buildTunnel(): void {
    const length = TUNNEL_END - TUNNEL_START;
    const mid = (TUNNEL_START + TUNNEL_END) / 2;
    const cx0 = this.river.centerX(TUNNEL_START);
    const cx1 = this.river.centerX(TUNNEL_END);
    const water0 = this.river.heightAt(TUNNEL_START);
    const water1 = this.river.heightAt(TUNNEL_END);
    const midX = (cx0 + cx1) / 2;
    const midY = (water0 + water1) / 2;
    const yaw = Math.atan2(-(cx1 - cx0), length);
    const pitch = Math.atan2(water0 - water1, length);

    const pipe = new THREE.Group();
    pipe.position.set(midX, midY - 0.4, -mid);
    pipe.rotation.set(-pitch, yaw, 0, 'YXZ');
    this.group.add(pipe);

    // Half pipe: the inside is what you see. Rotated so the open side faces down.
    const shell = new THREE.Mesh(
      new THREE.CylinderGeometry(TUNNEL_RADIUS, TUNNEL_RADIUS, length, 24, 1, true, -Math.PI / 2, Math.PI),
      new THREE.MeshStandardMaterial({ color: '#6f716c', roughness: 0.95, side: THREE.DoubleSide }),
    );
    // Lay the cylinder along z with its open half facing down: an arch.
    shell.rotation.x = -Math.PI / 2;
    shell.castShadow = true;
    shell.receiveShadow = true;
    pipe.add(shell);

    // Concrete rings at both mouths.
    const ringMaterial = new THREE.MeshStandardMaterial({ color: '#a9aaa3', roughness: 0.9 });
    for (const end of [-1, 1]) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(TUNNEL_RADIUS + 0.15, 0.28, 8, 28, Math.PI), ringMaterial);
      ring.position.z = end * (length / 2);
      ring.castShadow = true;
      pipe.add(ring);
    }

    // Lamps along the ceiling.
    const lampMaterial = new THREE.MeshStandardMaterial({ color: '#332200', emissive: '#ffcf6b', emissiveIntensity: 2.2 });
    for (let k = 1; k < 4; k += 1) {
      const lamp = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.12, 0.25), lampMaterial);
      lamp.position.set(0, TUNNEL_RADIUS - 0.1, -length / 2 + (length * k) / 4);
      pipe.add(lamp);
    }

    // Road embankment on top: earth, tarmac and a railing.
    const bankWidth = 30;
    const earth = new THREE.Mesh(
      new THREE.BoxGeometry(bankWidth, 2, length - 1),
      new THREE.MeshStandardMaterial({ color: '#5f9a45', roughness: 0.95, flatShading: true }),
    );
    earth.position.set(0, TUNNEL_RADIUS + 1.1, 0);
    earth.receiveShadow = true;
    pipe.add(earth);
    const road = new THREE.Mesh(
      new THREE.BoxGeometry(bankWidth, 0.1, length * 0.55),
      new THREE.MeshStandardMaterial({ color: '#3e4146', roughness: 0.9 }),
    );
    road.position.set(0, TUNNEL_RADIUS + 2.15, 0);
    pipe.add(road);
    const railMaterial = new THREE.MeshStandardMaterial({ color: '#d8d8d2', roughness: 0.6 });
    for (const edge of [-1, 1]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(bankWidth, 0.12, 0.12), railMaterial);
      rail.position.set(0, TUNNEL_RADIUS + 2.75, edge * (length / 2 - 0.8));
      pipe.add(rail);
      for (let p = -3; p <= 3; p += 1) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.6, 0.1), railMaterial);
        post.position.set(p * 2.2, TUNNEL_RADIUS + 2.45, edge * (length / 2 - 0.8));
        pipe.add(post);
      }
    }
    // Face walls at each mouth: one solid face with an arch-shaped hole.
    const half = bankWidth / 2;
    const top = TUNNEL_RADIUS + 2.2;
    const face = new THREE.Shape();
    face.moveTo(-half, -0.4);
    face.lineTo(half, -0.4);
    face.lineTo(half, top);
    face.lineTo(-half, top);
    face.lineTo(-half, -0.4);
    const hole = new THREE.Path();
    hole.moveTo(-TUNNEL_RADIUS, -0.4);
    hole.lineTo(TUNNEL_RADIUS, -0.4);
    hole.lineTo(TUNNEL_RADIUS, 0);
    hole.absarc(0, 0, TUNNEL_RADIUS, 0, Math.PI, false);
    hole.lineTo(-TUNNEL_RADIUS, -0.4);
    face.holes.push(hole);
    const faceGeometry = new THREE.ExtrudeGeometry(face, { depth: 0.5, bevelEnabled: false, curveSegments: 20 });
    const wallMaterial = new THREE.MeshStandardMaterial({ color: '#8a8b84', roughness: 0.95, flatShading: true });
    for (const end of [-1, 1]) {
      const wall = new THREE.Mesh(faceGeometry, wallMaterial);
      wall.position.z = end * (length / 2) - 0.25;
      wall.castShadow = true;
      wall.receiveShadow = true;
      pipe.add(wall);
    }
  }

  /** The split's island: a grassy lens between the channels, a few trees, and a sign at its nose. */
  private buildIsland(): void {
    const step = 0.5;
    const rows: { d: number; ix: number; iw: number; top: number; left: number; right: number }[] = [];
    for (let d = SPLIT_START; d <= SPLIT_END + 0.001; d += step) {
      const ix = this.river.islandX(d) ?? this.river.centerX(d) + 1.2;
      const iw = this.river.islandHalfWidth(d);
      const left = this.river.heightAt(d, ix - iw - 0.01);
      const right = this.river.heightAt(d, ix + iw + 0.01);
      rows.push({ d, ix, iw, top: Math.max(left, right), left, right });
    }
    // Cross-section: under the left water, left lip, crown, right lip, under the right water.
    const positions: number[] = [];
    const colors: number[] = [];
    const color = new THREE.Color();
    const push = (x: number, y: number, z: number, hex: string) => {
      positions.push(x, y, z);
      color.set(hex);
      colors.push(color.r, color.g, color.b);
    };
    for (const row of rows) {
      const lift = Math.min(0.55, row.iw * 0.5);
      push(row.ix - row.iw - 0.3, row.left - 0.4, -row.d, '#6d5a3c');
      push(row.ix - row.iw * 0.7, row.top + lift * 0.8, -row.d, '#8a7650');
      push(row.ix, row.top + lift, -row.d, '#5f9a45');
      push(row.ix + row.iw * 0.7, row.top + lift * 0.8, -row.d, '#8a7650');
      push(row.ix + row.iw + 0.3, row.right - 0.4, -row.d, '#6d5a3c');
    }
    const cols = 5;
    const indices: number[] = [];
    for (let r = 0; r < rows.length - 1; r += 1) {
      for (let c = 0; c < cols - 1; c += 1) {
        const a = r * cols + c;
        const b = a + 1;
        const e = a + cols;
        const f = e + 1;
        indices.push(a, b, e, b, f, e);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    const island = new THREE.Mesh(
      geometry,
      new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, flatShading: true, side: THREE.DoubleSide }),
    );
    island.receiveShadow = true;
    this.group.add(island);

    // A few trees down the middle make the island a landmark from upstream.
    const trunkMaterial = new THREE.MeshStandardMaterial({ color: '#6b4a2f', roughness: 0.9 });
    const crownMaterial = new THREE.MeshStandardMaterial({ color: '#3d7d3a', roughness: 0.85, flatShading: true });
    for (const d of [106, 112, 118]) {
      const row = rows[Math.round((d - SPLIT_START) / step)];
      const size = 0.7 + this.rng() * 0.3;
      const base = row.top + Math.min(0.55, row.iw * 0.5);
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.12 * size, 0.18 * size, 1.1 * size, 6), trunkMaterial);
      trunk.position.set(row.ix, base + 0.55 * size, -d);
      const crown = new THREE.Mesh(new THREE.ConeGeometry(0.95 * size, 2.3 * size, 7), crownMaterial);
      crown.position.set(row.ix, base + 2 * size, -d);
      trunk.castShadow = true;
      crown.castShadow = true;
      this.group.add(trunk, crown);
    }

    this.buildSign(rows[Math.round(3 / step)]);
  }

  private buildSign(row: { d: number; ix: number; top: number }): void {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2D context for sign texture.');
    ctx.fillStyle = '#c8955a';
    ctx.fillRect(0, 0, 256, 128);
    ctx.strokeStyle = '#7a5230';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, 248, 120);
    ctx.fillStyle = '#3b2412';
    ctx.textAlign = 'center';
    ctx.font = 'bold 34px "Avenir Next", Avenir, sans-serif';
    ctx.fillText('◀ SAFE', 128, 52);
    ctx.fillStyle = '#b3261e';
    ctx.fillText('SHORTCUT ▶', 128, 98);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;

    const sign = new THREE.Group();
    const board = new THREE.Mesh(
      new THREE.BoxGeometry(1.6, 0.8, 0.08),
      [
        new THREE.MeshStandardMaterial({ color: '#a8743f' }),
        new THREE.MeshStandardMaterial({ color: '#a8743f' }),
        new THREE.MeshStandardMaterial({ color: '#a8743f' }),
        new THREE.MeshStandardMaterial({ color: '#a8743f' }),
        new THREE.MeshStandardMaterial({ color: '#a8743f' }),
        new THREE.MeshStandardMaterial({ map: texture, roughness: 0.8 }),
      ],
    );
    // Face upstream (+z) toward the approaching duck.
    board.rotation.y = Math.PI;
    board.position.y = 1.5;
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.07, 1.5, 8),
      new THREE.MeshStandardMaterial({ color: '#7a5230' }),
    );
    // Behind the board so it doesn't cover the lettering.
    post.position.set(0, 0.75, -0.12);
    board.castShadow = true;
    post.castShadow = true;
    sign.add(board, post);
    sign.position.set(row.ix, row.top + 0.2, -row.d);
    this.group.add(sign);
  }

  private buildWhirlpools(): void {
    const texture = makeSpiralTexture();
    for (const pool of this.river.whirlpools) {
      const decal = new THREE.Mesh(
        new THREE.CircleGeometry(pool.radius, 40),
        new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false }),
      );
      decal.rotation.x = -Math.PI / 2;
      decal.position.set(pool.x, this.river.heightAt(pool.d) + 0.025, pool.z);
      decal.renderOrder = 1;
      this.whirlDecals.push(decal);
      this.group.add(decal);
    }
  }

  private buildTrees(): void {
    const spots: THREE.Vector3[] = [];
    for (let d = RIVER_D_MIN - 6; d < RIVER_D_MAX + 6; d += 2.6) {
      for (const side of [-1, 1]) {
        if (this.rng() < 0.35) continue;
        const out = 3 + this.rng() * 14;
        const td = d + this.rng() * 2;
        if (td > TUNNEL_START - 6 && td < TUNNEL_END + 6 && out < 10) continue;
        spots.push(
          new THREE.Vector3(this.river.xAt(td, side) + side * out, this.river.heightAt(td, side * Infinity) + 0.5, -td),
        );
      }
    }
    const trunkGeometry = new THREE.CylinderGeometry(0.14, 0.2, 1.2, 6);
    const crownGeometry = new THREE.ConeGeometry(1.1, 2.6, 7);
    const trunks = new THREE.InstancedMesh(
      trunkGeometry,
      new THREE.MeshStandardMaterial({ color: '#6b4a2f', roughness: 0.9 }),
      spots.length,
    );
    const crowns = new THREE.InstancedMesh(
      crownGeometry,
      new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.85, flatShading: true }),
      spots.length,
    );
    const matrix = new THREE.Matrix4();
    const quat = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const pos = new THREE.Vector3();
    const color = new THREE.Color();
    const greens = ['#2f6b35', '#3d7d3a', '#2a5e3b', '#4b8a3d'];
    spots.forEach((spot, i) => {
      const size = 0.8 + this.rng() * 0.8;
      scale.setScalar(size);
      pos.set(spot.x, spot.y + 0.6 * size, spot.z);
      matrix.compose(pos, quat, scale);
      trunks.setMatrixAt(i, matrix);
      pos.set(spot.x, spot.y + 2.3 * size, spot.z);
      matrix.compose(pos, quat, scale);
      crowns.setMatrixAt(i, matrix);
      crowns.setColorAt(i, color.set(greens[Math.floor(this.rng() * greens.length)]));
    });
    trunks.castShadow = true;
    crowns.castShadow = true;
    this.group.add(trunks, crowns);
  }

  private buildGate(d: number, kind: 'finish'): void {
    const left = this.river.xAt(d, -1) - 0.6;
    const right = this.river.xAt(d, 1) + 0.6;
    const width = right - left;
    const base = this.river.heightAt(d);
    const postMaterial = new THREE.MeshStandardMaterial({ color: kind === 'finish' ? '#e8e2d0' : '#e2533b' });
    for (const x of [left, right]) {
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 4, 10), postMaterial);
      post.position.set(x, base + 2, -d);
      post.castShadow = true;
      this.group.add(post);
    }
    let bannerMaterial: THREE.Material;
    if (kind === 'finish') {
      const texture = makeCheckerTexture();
      texture.repeat.set(width / 4, 1);
      bannerMaterial = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.7 });
    } else {
      bannerMaterial = new THREE.MeshStandardMaterial({ color: '#e2533b', roughness: 0.7 });
    }
    const banner = new THREE.Mesh(new THREE.BoxGeometry(width, 0.7, 0.12), bannerMaterial);
    banner.position.set((left + right) / 2, base + 3.7, -d);
    banner.castShadow = true;
    this.group.add(banner);

    if (kind === 'finish') {
      const lineTexture = makeCheckerTexture();
      lineTexture.repeat.set(width / 2, 1);
      const line = new THREE.Mesh(
        new THREE.PlaneGeometry(width, 0.5),
        new THREE.MeshBasicMaterial({ map: lineTexture, transparent: true, opacity: 0.85, depthWrite: false }),
      );
      line.rotation.x = -Math.PI / 2;
      line.position.set((left + right) / 2, base + 0.03, -d);
      line.renderOrder = 1;
      this.group.add(line);
    }
  }

  dispose(): void {
    disposeObject3D(this.group);
  }
}
